import { useState, useEffect, useCallback, useMemo } from 'react';
import { saveMaestroEntidad } from '../../../../../firebase/facturacionUtils';
import { onlyNumbers } from '../../helpers';

const INITIAL_FORM_DATA = {
    titular: '',
    cuitTitular: '',
    razonSocial: '',
    cuit: '',
    condicionIva: '',
    email: '',
    telefono: '',
    banco: '',
    tipoCuenta: '',
    numeroCuenta: '',
    cbu: '',
    nombre: ''
};

export const useModalMaestroEntidad = ({ show, onClose, onSuccess, tipoEntidad, initialData = null }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [subconceptos, setSubconceptos] = useState([]);
    const [nuevoSubconceptoInput, setNuevoSubconceptoInput] = useState('');

    useEffect(() => {
        if (show) {
            if (initialData) {
                setFormData({
                    titular: initialData.titular || '',
                    cuitTitular: initialData.cuitTitular || '',
                    razonSocial: initialData.razonSocial || initialData.nombre || '',
                    cuit: initialData.cuit || '',
                    condicionIva: initialData.condicionIva || '',
                    email: initialData.email || '',
                    telefono: initialData.telefono || '',
                    banco: initialData.banco || '',
                    tipoCuenta: initialData.tipoCuenta || '',
                    numeroCuenta: initialData.numeroCuenta || '',
                    cbu: initialData.cbu || '',
                    nombre: initialData.nombre || ''
                });
                setSubconceptos(Array.isArray(initialData.subconceptos) ? initialData.subconceptos : []);
            } else {
                setFormData(INITIAL_FORM_DATA);
                setSubconceptos([]);
            }
            setNuevoSubconceptoInput('');
            setError(null);
        }
    }, [initialData, show, tipoEntidad]);

    const config = useMemo(() => {
        switch (tipoEntidad) {
            case 'empresa_clientes':
                return { title: 'Cliente de Facturación', subtitle: 'Padrón de Clientes / Receptores de Comprobantes' };
            case 'empresa_proveedores':
                return { title: 'Proveedor / Prestador', subtitle: 'Padrón de Proveedores de Servicios y Compras' };
            case 'empresa_cuentas':
                return { title: 'Cuenta Bancaria / Tesorería', subtitle: 'Entidad, Titular, Tipo de Cuenta y CBU/CVU' };
            case 'empresa_areas':
                return { title: 'Área / Centro de Costo', subtitle: 'Estructura Organizativa e Identificación Fiscal' };
            case 'empresa_conceptos_facturas':
                return { title: 'Concepto de Facturación', subtitle: 'Categorías de Ingresos y Venta de Servicios' };
            case 'empresa_conceptos_gastos':
                return { title: 'Concepto & Subconceptos de Gastos', subtitle: 'Estructura Jerárquica de Egresos y Compras' };
            default:
                return { title: 'Registro Maestro', subtitle: 'Administración de Maestros' };
        }
    }, [tipoEntidad]);

    const handleAddSubconcepto = useCallback(() => {
        const val = nuevoSubconceptoInput.trim().toUpperCase();
        if (!val) return;
        if (subconceptos.includes(val)) {
            setError("El subconcepto ya se encuentra agregado en la lista.");
            return;
        }
        setSubconceptos(prev => [...prev, val]);
        setNuevoSubconceptoInput('');
        setError(null);
    }, [nuevoSubconceptoInput, subconceptos]);

    const handleRemoveSubconcepto = useCallback((indexToRemove) => {
        setSubconceptos(prev => prev.filter((_, idx) => idx !== indexToRemove));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        let payload = {};

        if (tipoEntidad === 'empresa_clientes' || tipoEntidad === 'empresa_proveedores') {
            if (!formData.razonSocial.trim()) return setError("La Razón Social es obligatoria.");
            payload = {
                razonSocial: formData.razonSocial.trim().toUpperCase(),
                cuit: formData.cuit.trim(),
                condicionIva: formData.condicionIva,
                email: formData.email.trim(),
                telefono: formData.telefono.trim()
            };
        } else if (tipoEntidad === 'empresa_cuentas') {
            if (!formData.banco.trim()) return setError("El Banco / Entidad Financiera es obligatorio.");
            if (!formData.titular.trim()) return setError("El Titular / Razón Social de la cuenta es obligatorio.");
            if (!formData.tipoCuenta) return setError("Debe seleccionar un Tipo de Cuenta.");
            if (formData.cbu && formData.cbu.length !== 22) return setError("El CBU / CVU debe contener exactamente 22 dígitos numéricos.");

            const bancoClean = formData.banco.trim().toUpperCase();
            const titularClean = formData.titular.trim().toUpperCase();
            const tipoCuentaClean = formData.tipoCuenta;
            const nombreDisplay = `${bancoClean} • ${titularClean} (${tipoCuentaClean})`;

            payload = {
                banco: bancoClean,
                titular: titularClean,
                cuitTitular: formData.cuitTitular.trim(),
                tipoCuenta: tipoCuentaClean,
                numeroCuenta: formData.numeroCuenta.trim(),
                cbu: formData.cbu.trim(),
                nombre: nombreDisplay
            };
        } else if (tipoEntidad === 'empresa_areas') {
            if (!formData.nombre.trim()) return setError("El Nombre del Área es obligatorio.");
            payload = {
                nombre: formData.nombre.trim().toUpperCase(),
                cuit: formData.cuit.trim()
            };
        } else if (tipoEntidad === 'empresa_conceptos_facturas') {
            if (!formData.nombre.trim()) return setError("El Nombre del Concepto es obligatorio.");
            payload = {
                nombre: formData.nombre.trim().toUpperCase()
            };
        } else if (tipoEntidad === 'empresa_conceptos_gastos') {
            if (!formData.nombre.trim()) return setError("El Nombre del Concepto de Gasto es obligatorio.");
            payload = {
                nombre: formData.nombre.trim().toUpperCase(),
                subconceptos
            };
        }

        setLoading(true);
        try {
            await saveMaestroEntidad(tipoEntidad, payload, initialData?.id || null);
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            console.error("Error al guardar maestro:", err);
            setError("Ocurrió un error técnico al intentar guardar el registro.");
        } finally {
            setLoading(false);
        }
    };

    return {
        formData, setFormData,
        subconceptos,
        nuevoSubconceptoInput, setNuevoSubconceptoInput,
        loading, error, config,
        handleAddSubconcepto, handleRemoveSubconcepto,
        handleSubmit
    };
};